import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { toast } from "sonner";
import { 
  Bell,
  BellRinging,
  Eye,
  Check,
  Lightning,
  TrendUp,
  TrendDown,
  Clock
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

// Alert Item Component
const AlertItem = ({ alert, onMarkRead, onView }) => {
  const getIcon = () => {
    switch (alert.alert_type) {
      case 'high_score':
        return <Lightning size={18} className="text-amber-500" weight="fill" />;
      case 'entry_signal':
        return <TrendUp size={18} className="text-emerald-500" weight="fill" />;
      case 'stop_triggered':
        return <TrendDown size={18} className="text-red-500" weight="fill" />;
      default:
        return <BellRinging size={18} className="text-blue-500" weight="fill" />;
    }
  };

  const getTypeLabel = () => {
    switch (alert.alert_type) {
      case 'high_score':
        return 'Score Élevé';
      case 'entry_signal':
        return 'Signal d\'Entrée';
      case 'stop_triggered':
        return 'Stop Déclenché';
      default:
        return 'Notification';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div 
      className={`p-4 border-b border-slate-100 last:border-0 ${!alert.is_read ? 'bg-blue-50/50' : ''}`}
      data-testid={`alert-item-${alert.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-sm ${!alert.is_read ? 'bg-blue-100' : 'bg-slate-100'}`}>
          {getIcon()}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-heading font-bold text-slate-900">{alert.symbol}</span>
            <Badge variant="outline" className="text-xs font-data">
              Score: {alert.score}
            </Badge>
            {!alert.is_read && (
              <Badge className="bg-blue-100 text-blue-700 text-xs">Nouveau</Badge>
            )}
          </div>
          
          <p className="text-sm text-slate-600 font-body mb-2">{alert.message}</p>
          
          <div className="flex items-center gap-4 text-xs text-slate-400">
            <span className="flex items-center gap-1">
              <Bell size={12} />
              {getTypeLabel()}
            </span>
            <span className="flex items-center gap-1">
              <Clock size={12} />
              {formatDate(alert.created_at)}
            </span>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => onView(alert)}
            className="rounded-sm"
            data-testid={`view-alert-${alert.id}`}
          >
            <Eye size={16} />
          </Button>
          {!alert.is_read && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => onMarkRead(alert.id)}
              className="rounded-sm"
              data-testid={`mark-read-${alert.id}`}
            >
              <Check size={16} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default function Alerts() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread

  useEffect(() => {
    fetchAlerts();
  }, [filter]);

  const fetchAlerts = async () => {
    try {
      const url = filter === 'unread' 
        ? `${API}/alerts?unread_only=true`
        : `${API}/alerts`;
      const response = await fetch(url);
      const data = await response.json();
      setAlerts(data);
    } catch (error) {
      console.error("Error fetching alerts:", error);
      toast.error("Erreur lors du chargement des alertes");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (alertId) => {
    try {
      await fetch(`${API}/alerts/${alertId}/read`, { method: 'POST' });
      setAlerts(prev => 
        prev.map(a => a.id === alertId ? { ...a, is_read: true } : a)
      );
      toast.success("Alerte marquée comme lue");
    } catch (error) {
      console.error("Error marking alert as read:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleView = (alert) => {
    navigate(`/opportunity/${alert.symbol}`);
  };

  const unreadCount = alerts.filter(a => !a.is_read).length;

  if (loading) {
    return (
      <div className="space-y-4" data-testid="alerts-loading">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div data-testid="alerts-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Alertes</h1>
          <p className="text-sm text-slate-500 font-body mt-1">
            {unreadCount > 0 
              ? `${unreadCount} alerte${unreadCount > 1 ? 's' : ''} non lue${unreadCount > 1 ? 's' : ''}`
              : 'Toutes les alertes sont lues'
            }
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('all')}
            className="rounded-sm"
            data-testid="filter-all-btn"
          >
            Toutes
          </Button>
          <Button 
            variant={filter === 'unread' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('unread')}
            className="rounded-sm"
            data-testid="filter-unread-btn"
          >
            Non lues ({unreadCount})
          </Button>
        </div>
      </div>

      {/* Alerts List */}
      <Card className="border-slate-200 rounded-sm shadow-none">
        <CardContent className="p-0">
          {alerts.length === 0 ? (
            <div className="text-center py-12">
              <Bell size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 font-body">
                {filter === 'unread' 
                  ? 'Aucune alerte non lue'
                  : 'Aucune alerte'
                }
              </p>
              <p className="text-sm text-slate-400 font-body mt-1">
                Les alertes apparaîtront ici lorsque des opportunités qualifiées seront détectées
              </p>
            </div>
          ) : (
            alerts.map((alert) => (
              <AlertItem 
                key={alert.id}
                alert={alert}
                onMarkRead={handleMarkRead}
                onView={handleView}
              />
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
